/**
 * The two ways to clear a Candidate Requests card, and the difference between them.
 *
 * Before Aug 20 2026 there was one button doing three things at once — mark read, remove from the
 * inbox, add the `Spaces` label — so tidying the board also emptied the recruiter's inbox whether
 * they meant it or not. Both actions still record the same triage (our own record, not Gmail, is
 * what stops a request coming back) and both still mark the mail read; only the inbox differs, and
 * the labels say which.
 *
 * One component because the card renders these in two places — the collapsed action column and the
 * expanded footer — and two copies of a pair of buttons is how their wording drifts apart.
 */
import { Button } from '@finn-auto/design-system/atoms/button';

const REMOVE_HELP =
  'Marks the mail read and clears it from this overview, but leaves it in your Gmail inbox.';

export function ClearActions({
  busy,
  onMarkRead,
  onRemoveFromOverview,
  stacked = false,
}: {
  busy: boolean;
  /** Read, labelled, and OUT of the inbox. */
  onMarkRead: () => void;
  /** Read and done, but the mail stays in the inbox. */
  onRemoveFromOverview: () => void;
  /** True in the card's narrow action column, where the buttons are full-width and may wrap. */
  stacked?: boolean;
}) {
  const shape = stacked ? 'w-full whitespace-normal text-center leading-tight' : undefined;
  return (
    <>
      <Button
        size="sm"
        variant={stacked ? 'outline' : 'ghost'}
        className={shape}
        onClick={onMarkRead}
        // `loading` only where this is the prominent control; a spinner on a ghost button in a row
        // of four reads as the whole row being busy.
        loading={stacked ? busy : undefined}
        disabled={stacked ? undefined : busy}
      >
        Archive in Gmail
      </Button>
      {/*
        Secondary to the archive on purpose: most cleared requests really are finished, and this is
        the narrower choice — done with on the board, still in the inbox.
      */}
      <Button
        size="sm"
        variant="ghost"
        className={shape}
        onClick={onRemoveFromOverview}
        disabled={busy}
        title={REMOVE_HELP}
      >
        Remove from overview
      </Button>
    </>
  );
}
